import React from 'react';

import PropTypes, {
  bool,
  func,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import { FaListUl, FaRegCommentAlt } from 'react-icons/fa';
import Sticky from 'react-stickynode';
import styled, { css } from 'styled-components';

import {
  useBreakpoint,
  Collapsible,
  useCollapsibleState,
} from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';

import { mobile } from '../../styles';
import {
  checkIsCollectible,
  getSetItemLinkedProduct,
} from '../../utils/productDetail';
import { Component as ProductDescription } from '../ProductDescription';
import { Component as ProductDetailColorSwatches } from '../ProductDetailColorSwatches';
import { Container as ProductDetailReviews } from '../ProductDetailReviews';
import { Container as ProductDetailSize } from '../ProductDetailSize';
import { Component as ProductDetailStars } from '../ProductDetailStars';
import { Component as ProductImage } from '../ProductImage';
import { Component as ProductLink } from '../ProductLink';
import { Component as ProductSetDetailOptionSummary } from '../ProductSetDetailOptionSummary';
import { Component as StaticIcon } from '../StaticIcon';

const SetItemWrapper = styled.div``;

const SetItem = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  margin-top: ${props => props.theme.spacing.small}px;

  padding: ${props => props.theme.spacing.small}px;

  border: solid 1px ${props => props.theme.colors.stone};
`;

const SetItemHeader = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  max-width: 100%;
`;

const RowCollapsible = styled(Row)`
  flex-direction: row;
`;

const SetItemName = styled.div`
  margin-bottom: ${props => (props.isSticky ? 0 : props.theme.spacing.small)}px;
  align-self: center;

  ${({ theme }) => theme.subHeader.variants.subHeading2Uppercase.textStyles}
  color: ${props => props.theme.colors.gunmetal};
`;

const StickySingleItem = styled(Sticky)`
  max-height: 1px;

  &.active {
    width: 100%;
  }

  & > .sticky-inner-wrapper {
    z-index: 1;
  }
  &.active > .sticky-inner-wrapper {
    z-index: 2;
    padding-top: 0px;
    min-width: 200px;
    width: 100%;
    transition-property: transform;
    transition-duration: 300ms;
  }
`;

const SetItemHeaderFixed = styled.div`
  display: flex;
  box-shadow: 0 2px 3px 0 ${props => props.theme.colors.lightShadow1};

  border: solid 1px ${props => props.theme.colors.stone};

  background-color: ${props => props.theme.colors.white};
  width: calc(100% + 32px);
  margin-left: -16px;
`;

const SetItemImageButton = styled.button`
  cursor: pointer;
  margin-right: ${props => (props.isSticky ? props.theme.spacing.small : 12)}px;
  margin-bottom: ${props => (props.isSticky ? 4 : props.theme.spacing.small)}px;
  margin-top: ${props => (props.isSticky ? 4 : 0)}px;
  margin-left: ${props => (props.isSticky ? 4 : 0)}px;
  width: ${props => (props.isSticky ? 40 : 81)}px;
  height: ${props => (props.isSticky ? 40 : 81)}px;

  ${mobile`
    width: 115px;
    height: 115px;
  `};

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.theme.focusColor};
  }
`;

const SetItemNameLink = styled.a`
  color: inherit;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const SetItemProductImage = styled(ProductImage)`
  display: block;
  height: ${props => (props.isSticky ? 40 : 81)}px;
  width: auto;

  ${mobile`
    width: auto;
    height: 115px;
  `};
`;

const EmptySticky = styled.div`
  width: 1px;
  height: 1px;
`;

const ProductSetDetailOptionSummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledCollapsible = styled(Collapsible)``;

const CollapsibleButton = styled(Collapsible.Button)`
  ${props => props.theme.collapsibleTrigger.defaultStyle};
  font-size: 12px;
  font-weight: 300;
  padding: ${props => props.theme.spacing.tiny}px
    ${props => props.theme.spacing.small}px;

  border: solid 1px ${props => props.theme.colors.stone};

  border-top: none;

  ${props =>
    props.isOpen &&
    css`
      border-bottom: none;
    `}

  &:focus {
    outline: none;
  }
`;

const CollapsibleContent = styled(Collapsible.Content)`
  border: solid 1px ${props => props.theme.colors.stone};

  border-top: none;

  &[aria-hidden='true'] {
    padding: 0;
    border: none;
  }
`;

const DownStaticIcon = styled(StaticIcon).attrs({
  type: 'caret',
  size: 8,
})`
  margin-left: auto;
  color: ${props => props.theme.colors.default};
`;

const UpStaticIcon = styled(DownStaticIcon)`
  transform: rotate(180deg);
`;

const StyledProductDescription = styled(ProductDescription)`
  padding: 8px ${props => props.theme.spacing.small}px
    ${props => props.theme.spacing.moderate}px;
  ${ProductDescription.DescriptionContent} {
    font-size: 12px;
    font-weight: 300;
    line-height: 1.33;
    letter-spacing: 0.3px;
  }
`;
const SetProductDetailReviews = styled(ProductDetailReviews)`
  padding: 8px ${props => props.theme.spacing.small}px
    ${props => props.theme.spacing.moderate}px;
`;

const ProductDetailLabel = styled.div`
  ${({ theme }) => theme.paragraph.variants.paragraph3.textStyles}
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const ProductReviewsLabel = styled(ProductDetailLabel)`
  align-items: center;
`;

const ReviewIcon = styled(FaRegCommentAlt)`
  font-size: 12px;
`;

const ListIcon = styled(FaListUl)`
  font-size: 10px;
`;

const StyledProductDetailStars = styled(ProductDetailStars)``;

const AverageRating = styled.div`
  & ${StyledProductDetailStars} {
    margin-bottom: 0;
  }

  & ${StyledProductDetailStars.StyledStars} {
  }

  & ${StyledProductDetailStars.AverageRating} {
    display: none;
  }
`;

function ToggleButton({ label }) {
  const { isOpen } = useCollapsibleState();

  return (
    <CollapsibleButton isOpen={isOpen}>
      {label}
      {isOpen ? <UpStaticIcon /> : <DownStaticIcon />}
    </CollapsibleButton>
  );
}

ToggleButton.propTypes = {
  label: PropTypes.node,
};

export default function ProductDetailSetItem({
  extraQueryParams,
  handleColorChange,
  handleItemHeaderStickyState,
  handleSizeChange,
  index,
  isBorderfreeCustomer,
  isCombinedSet,
  isItemHeaderFixed,
  isPreorderable,
  isSoldOut,
  isUnbreakableSet,
  isXtraVipBox = false,
  reviews,
  scrollToTopReviews,
  selectedSizeList,
  selections,
  setItem,
  showImageModal,
  topItemHeaderPosition = 0,
  trackProductSetDetailsOpen,
  trackProductSetDetailsClose,
  trackProductSetReviewsOpen,
  trackProductSetReviewsClose,
  updateReviewRef,
  updateSizeRef,
}) {
  const { isDesktop } = useBreakpoint();
  const id = setItem.master_product_id;
  const selectedSize = selectedSizeList[id]?.size;
  const isCollectible = checkIsCollectible(setItem);
  const currentSelection = selections[id];
  const setItemReviews = reviews ? reviews[id] : null;
  const isLinkedSize =
    currentSelection.size.sizeProduct &&
    currentSelection.size.sizeProduct.is_linked_size;

  const setItemLinkedProduct = getSetItemLinkedProduct(
    setItem,
    currentSelection.color.value,
    isLinkedSize
  );

  const setItemLabel = isLinkedSize
    ? setItemLinkedProduct.label
    : setItem.label;

  const detailsToggleLabel = (
    <ProductDetailLabel>
      <ListIcon />
      <FormattedMessage
        id="site_pdp.product_details_title"
        defaultMessage="Details"
      />
    </ProductDetailLabel>
  );

  const reviewsToggleLabel = (
    <ProductReviewsLabel data-autotag="reviews-count" ref={updateReviewRef(id)}>
      <ReviewIcon />
      <FormattedMessage
        id="site_pdp.review_count_label"
        defaultMessage="{count} {count, plural, one {Review} other {Reviews}}"
        values={{
          count: (setItemReviews && setItemReviews.counts.reviewCount) || 0,
        }}
      />
      <AverageRating>
        <StyledProductDetailStars reviews={setItemReviews} showAverageRating />
      </AverageRating>
    </ProductReviewsLabel>
  );

  const renderSummary = () => (
    <ProductSetDetailOptionSummaryWrapper>
      <ProductSetDetailOptionSummary
        currentSelection={currentSelection}
        isSoldOut={isSoldOut}
        setItem={setItem}
        setIndex={index}
      >
        <ProductDetailColorSwatches
          id={setItem.master_product_id}
          isBorderfreeCustomer={isBorderfreeCustomer}
          isSoldOut={isSoldOut}
          isUnbreakableSet={isUnbreakableSet}
          isXtraVipBox={isXtraVipBox}
          limit={10}
          masterProduct={setItem}
          onChangeColor={handleColorChange}
          selectedColor={currentSelection.color.value}
          selectedColorLabel={currentSelection.color.name}
          selectedSize={selectedSize}
          showSelectedOnly={false}
          setId={setItem.master_product_id}
        />
        <ProductDetailSize
          disabled={isXtraVipBox && isSoldOut}
          error={currentSelection.size.error}
          hideLabel
          hideQuantity
          id={setItem.master_product_id}
          innerRef={() => updateSizeRef(setItem.master_product_id)}
          isSoldOut={isSoldOut}
          onChangeSize={handleSizeChange}
          masterProduct={setItem}
          selectedColorLabel={currentSelection.color.name}
          selectedProductQuantity={1}
          size={currentSelection.size}
          sizeList={currentSelection.color.sizes}
          isSet
          isCombinedSet={isCombinedSet}
          setId={setItem.master_product_id}
          setIndex={index}
        />
      </ProductSetDetailOptionSummary>
    </ProductSetDetailOptionSummaryWrapper>
  );

  const renderMobileHeader = () => {
    return (
      <RowCollapsible data-testid="set-item-mobile-header">
        <SetItemImageButton
          data-testid="set-item-image-button"
          onClick={() =>
            showImageModal(
              isLinkedSize ? setItemLinkedProduct : currentSelection.color
            )
          }
        >
          <SetItemProductImage
            product={
              isLinkedSize ? setItemLinkedProduct : currentSelection.color
            }
            size={6}
            angle="LAYDOWN"
          />
        </SetItemImageButton>
        {renderSummary()}
      </RowCollapsible>
    );
  };

  const renderDesktopHeader = () => (
    <>
      <Row>
        <StickySingleItem
          top={topItemHeaderPosition}
          bottomBoundary={`#set-reviews-${index}`}
          onStateChange={status =>
            handleItemHeaderStickyState(status, `item-${index}`)
          }
        >
          {isItemHeaderFixed ? (
            <SetItemHeaderFixed>
              <SetItemImageButton
                data-testid="set-item-image-button"
                onClick={() =>
                  showImageModal(
                    isLinkedSize ? setItemLinkedProduct : currentSelection.color
                  )
                }
                isSticky
              >
                <SetItemProductImage
                  isSticky
                  product={
                    isLinkedSize ? setItemLinkedProduct : currentSelection.color
                  }
                  angle="LAYDOWN"
                  size={6}
                />
              </SetItemImageButton>
              {isUnbreakableSet || isCollectible ? (
                <SetItemName
                  data-testid="isUnbreakableSet-SetItemName"
                  isSticky
                >
                  {setItemLabel}
                </SetItemName>
              ) : (
                <ProductLink
                  extraQueryParams={extraQueryParams}
                  product={isLinkedSize ? setItemLinkedProduct : setItem}
                  passHref
                >
                  <SetItemNameLink>
                    <SetItemName isSticky>{setItemLabel}</SetItemName>
                  </SetItemNameLink>
                </ProductLink>
              )}
            </SetItemHeaderFixed>
          ) : (
            <EmptySticky />
          )}
        </StickySingleItem>
      </Row>
      <RowCollapsible>
        <SetItemImageButton
          data-testid="set-item-image-button"
          onClick={() =>
            showImageModal(
              isLinkedSize ? setItemLinkedProduct : currentSelection.color
            )
          }
        >
          <SetItemProductImage
            product={
              isLinkedSize ? setItemLinkedProduct : currentSelection.color
            }
            angle="LAYDOWN"
            size={6}
          />
        </SetItemImageButton>
        {renderSummary()}
      </RowCollapsible>
    </>
  );

  return (
    <SetItemWrapper
      key={`pdp-set-reviews-${index}`}
      id={`set-reviews-${index}`}
      data-autotag="pdpSetsCount"
    >
      <SetItem>
        <SetItemHeader>
          <Row>
            {isUnbreakableSet || isCollectible ? (
              <SetItemName data-testid="isUnbreakableSet-SetItemName">
                {setItemLabel}
              </SetItemName>
            ) : (
              <ProductLink
                extraQueryParams={extraQueryParams}
                product={isLinkedSize ? setItemLinkedProduct : setItem}
                passHref
              >
                <SetItemNameLink>
                  <SetItemName>{setItemLabel}</SetItemName>
                </SetItemNameLink>
              </ProductLink>
            )}
          </Row>
          {isDesktop ? renderDesktopHeader() : renderMobileHeader()}
        </SetItemHeader>
      </SetItem>
      <StyledCollapsible
        data-testid="set-details"
        defaultOpen={false}
        onOpen={trackProductSetDetailsOpen}
        onClose={trackProductSetDetailsClose}
      >
        <ToggleButton label={detailsToggleLabel} />
        <CollapsibleContent>
          <StyledProductDescription
            tabs={false}
            details={
              isLinkedSize
                ? setItemLinkedProduct.medium_description
                : setItem.medium_description || ''
            }
            longDescription={
              isLinkedSize
                ? setItemLinkedProduct.long_description
                : setItem.long_description || ''
            }
            isSet
          />
        </CollapsibleContent>
      </StyledCollapsible>
      {isPreorderable ? null : (
        <StyledCollapsible
          data-testid="set-reviews"
          defaultOpen={false}
          onOpen={trackProductSetReviewsOpen}
          onClose={trackProductSetReviewsClose}
        >
          <ToggleButton label={reviewsToggleLabel} />
          <CollapsibleContent>
            <SetProductDetailReviews
              location="desktop"
              product={setItem}
              scrollToTopReviews={scrollToTopReviews}
              isSet
              isCombinedSet={isCombinedSet}
            />
          </CollapsibleContent>
        </StyledCollapsible>
      )}
    </SetItemWrapper>
  );
}

ProductDetailSetItem.propTypes = {
  extraQueryParams: object,
  handleColorChange: func,
  handleItemHeaderStickyState: func,
  handleSizeChange: func,
  index: number,
  isBorderfreeCustomer: bool,
  isCombinedSet: bool,
  isItemHeaderFixed: bool,
  isPreorderable: bool,
  isSoldOut: bool,
  isUnbreakableSet: bool,
  isXtraVipBox: bool,
  reviews: object,
  scrollToTopReviews: func,
  selectedSizeList: shape({
    size: shape({
      color: object,
      masterProductId: number,
      size: object,
    }),
  }),
  selections: shape({
    size: shape({
      sizeProduct: shape({
        is_linked_size: bool,
      }),
    }),
  }),
  setItem: shape({
    master_product_id: number,
    label: string,
    medium_description: string,
    long_description: string,
  }),
  showImageModal: func,
  topItemHeaderPosition: oneOfType([number, string]),
  trackProductSetDetailsOpen: func,
  trackProductSetDetailsClose: func,
  trackProductSetReviewsOpen: func,
  trackProductSetReviewsClose: func,
  updateReviewRef: func,
  updateSizeRef: func,
};
